import { zodResolver } from '@hookform/resolvers/zod';
import { FC, PropsWithChildren, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { getJourneyCurrentConversionEvent, getWorkflow } from 'modules/workflows/ducks';
import {
  conversionSchema,
  getDefaultConversions,
} from 'modules/journey/components/JourneyConversions';
import { BackendJourneyConversionEvent } from 'modules/workflows/types/workflow';
import { Rules } from 'modules/journey/components/RuleBuilder/types';
import { useExitRuleContext } from 'modules/workflows/context/ExitRuleContext';
import {
  DESCRIPTION_FIELD,
  LABELS_FIELD,
  NAME_FIELD,
  SettingsTab,
} from './SettingsTakeover.constants';
import { SettingsTakeoverProps } from './SettingsTakeover.types';
import { DROPDOWNS_LIST } from './Metrics/Metrics.constants';
import { getDefaultTileMetrics } from './Metrics/Metrics.utils';
import { publishedJourneyOptionsSchema } from '../PublishedJourneyOptions/publishedJourneyOptions.schema';
import { VISITOR_TRACKING_FIELD } from '../PublishedJourneyOptions/publishedJourneyOptions.constants';
import { useJourneyVisitorTrackingContext } from 'modules/workflows/context/JourneyVisitorTrackingContext';

const tileMetricsSchema = () =>
  DROPDOWNS_LIST.reduce(
    (acc, dropdown) => ({
      ...acc,
      [dropdown.actionName]: z.string().array(),
    }),
    {},
  );

const schema = z
  .object({
    currentTab: z.nativeEnum(SettingsTab),
    [SettingsTab.About]: z
      .object({
        [NAME_FIELD]: z.string(),
        [DESCRIPTION_FIELD]: z.string(),
        [LABELS_FIELD]: z.string().array().optional(),
      })
      .and(publishedJourneyOptionsSchema),
    [SettingsTab.Metrics]: z.object({
      ...tileMetricsSchema(),
    }),
    [SettingsTab.ExitRules]: z.custom<Rules>(),
  })
  .and(conversionSchema);

export type SettingsTakeoverFormSchemaType = z.infer<typeof schema>;

export const SettingsTakeoverFormProvider: FC<PropsWithChildren<SettingsTakeoverProps>> = ({
  focusedTab,
  children,
}) => {
  const { exitRules } = useExitRuleContext();
  const { description = '', tileMetrics = {}, name = '', labels = [] } = useSelector(getWorkflow);
  const currentConversion = useSelector(getJourneyCurrentConversionEvent);
  const defaultConversionValues = getDefaultConversions(
    currentConversion as BackendJourneyConversionEvent,
  );

  const { isJVTEnabled } = useJourneyVisitorTrackingContext();

  const methods = useForm<SettingsTakeoverFormSchemaType>({
    defaultValues: {
      currentTab: focusedTab || SettingsTab.About,
      [SettingsTab.About]: {
        [NAME_FIELD]: name,
        [DESCRIPTION_FIELD]: description,
        [LABELS_FIELD]: labels.map((label) => label.name),
        [VISITOR_TRACKING_FIELD]: isJVTEnabled || false,
      },
      [SettingsTab.Metrics]: {
        ...getDefaultTileMetrics(tileMetrics),
      },
      conversions: defaultConversionValues,
      [SettingsTab.ExitRules]: exitRules || [],
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    methods.reset(
      {
        ...methods.getValues(),
        [SettingsTab.ExitRules]: exitRules || [],
      },
      { keepDirty: false },
    );
  }, [JSON.stringify(exitRules)]);

  return <FormProvider {...methods}>{children} </FormProvider>;
};
